/** @jsx h */
import { component, h } from 'literaljs';
import { navigate } from 'literaljs-router';
import { playPop, playChime } from '../../utils/sounds';
import { copyToClipboard } from '../../utils/clipboard';
import { showToast } from '../../utils/toast';

const RouteExample = component({
	name: 'RouteExample',
	props: ['path', 'name', 'description', 'code'],
	methods() {
		return {
			copyCode(code) {
				copyToClipboard(code,
					() => { showToast('Copied to clipboard', { type: 'success' }); playChime(); },
					() => showToast('Failed to copy', { type: 'error' })
				);
			}
		};
	},
	render() {
		const { path, name, description, code } = this.props;
		return (
			<div class="route-example">
				<div class="route-path">
					<code class="route-path-text">{path}</code>
					<span class="route-name">{name}</span>
				</div>
				<p class="route-desc">{description}</p>
				<div class="route-code">
					<div class="route-code-header">
						<span>Definition</span>
						<button
							class="copy-btn"
							aria-label="Copy route definition"
							events={{ click: () => this.copyCode(code) }}
						>
							<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
								<rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
								<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
							</svg>
							Copy
						</button>
					</div>
					<pre><code>{code}</code></pre>
				</div>
			</div>
		);
	}
});

const INSTALL_CMD = 'npm install literaljs-router';

const QUICK_START = `import { Router, navigate } from 'literaljs-router';
import { App, h } from 'literaljs';
import { HomePage } from './pages/HomePage';
import { AboutPage } from './pages/AboutPage';

// Define routes
const routes = [
  { path: '/', render: () => h(HomePage) },
  { path: '/about', render: () => h(AboutPage) }
];

// Mount your app with routing
new App({
  render: () => h('div', null, [
    h('nav', null, [
      h('a', { href: '/', onClick: (e) => { e.preventDefault(); navigate('/'); } }, 'Home'),
      h('a', { href: '/about', onClick: (e) => { e.preventDefault(); navigate('/about'); } }, 'About')
    ]),
    h('main', null, Router({ routes }))
  ])
}).mount('root');`;

const NAVIGATE_EXAMPLE = `import { navigate } from 'literaljs-router';

// Inside any component method
goToHome() {
  navigate('/');
}

goToUser(userId) {
  navigate(\`/user/\${userId}\`);
}

goBack() {
  navigate(-1); // go back one entry
}`;

export default component({
	name: 'RouterDocs',
	state: { activeSection: 'overview', currentPath: '/' },
	mounted() {
		this._onPopState = () => {
			this.setState({ currentPath: window.location.pathname });
		};
		window.addEventListener('popstate', this._onPopState);
	},
	unmounted() {
		if (this._onPopState) window.removeEventListener('popstate', this._onPopState);
	},
	methods() {
		return {
			setSection(section) {
				playPop();
				this.setState({ activeSection: section });
			},
			copyCode(code) {
				copyToClipboard(code,
					() => { showToast('Copied to clipboard', { type: 'success' }); playChime(); },
					() => showToast('Failed to copy', { type: 'error' })
				);
			},
			goTo(path) {
				playPop();
				navigate(path);
				showToast('Navigated to ' + path, { type: 'success' });
			}
		};
	},
	render() {
		const { activeSection, currentPath } = this.getState();

		const sections = [
			{ id: 'overview', label: 'Overview' },
			{ id: 'routes', label: 'Routes' },
			{ id: 'navigation', label: 'Navigation' }
		];

		const features = [
			{ label: 'Zero dependencies', desc: 'Built on top of literaljs, nothing else' },
			{ label: 'Named parameters', desc: 'Dynamic route segments with :param syntax' },
			{ label: 'Programmatic navigation', desc: 'Imperative navigate() for state-driven routing' },
			{ label: 'Hash & history modes', desc: 'Works with history API or hashchange fallback' },
			{ label: 'Auto re-render', desc: 'Route changes trigger app.update() automatically' }
		];

		return (
			<div class="router-docs">
				<div class="router-header">
					<div class="router-badge">
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
							<circle cx="6" cy="6" r="2" />
							<circle cx="18" cy="18" r="2" />
							<path d="M8 6h6a4 4 0 0 1 4 4v6" />
						</svg>
						Router
					</div>
					<h2>LiteralJS Router</h2>
					<p>Client-side routing for single-page applications. Zero dependencies, named parameters, and programmatic navigation.</p>
				</div>

				<div class="install-block">
					<div class="install-block-label">Install</div>
					<code class="install-block-cmd">{INSTALL_CMD}</code>
					<button
						class="install-block-copy"
						aria-label="Copy install command"
						events={{ click: () => this.copyCode(INSTALL_CMD) }}
					>
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
							<rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
							<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
						</svg>
						Copy
					</button>
				</div>

				<div class="router-nav" role="tablist">
					{sections.map(section => (
						<button
							key={section.id}
							role="tab"
							aria-selected={activeSection === section.id}
							class={"router-nav-btn" + (activeSection === section.id ? " -active" : "")}
							events={{ click: () => this.setSection(section.id) }}
						>
							{section.label}
						</button>
					))}
				</div>

				<div class="router-content">
					{activeSection === 'overview' && (
						<div class="section-panel">
							<div class="features-grid">
								{features.map(f => (
									<div class="feature-item" key={f.label}>
										<div class="feature-item-name">{f.label}</div>
										<div class="feature-item-desc">{f.desc}</div>
									</div>
								))}
							</div>

							<h3 class="panel-heading">Quick Start</h3>
							<div class="code-block">
								<div class="code-block-header">
									<span>main.js</span>
									<button
										class="copy-btn"
										aria-label="Copy quick start code"
										events={{ click: () => this.copyCode(QUICK_START) }}
									>
										<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
											<rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
											<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
										</svg>
										Copy
									</button>
								</div>
								<pre class="code-block-body"><code>{QUICK_START}</code></pre>
							</div>
						</div>
					)}

					{activeSection === 'routes' && (
						<div class="section-panel">
							<h3 class="panel-heading">Route Types</h3>
							<div class="route-examples">
								<RouteExample
									path="/"
									name="Home"
									description="Landing page route. Matched exactly."
									code="{ path: '/', render: () => h(HomePage) }"
								/>
								<RouteExample
									path="/about"
									name="About"
									description="Static route. Matched exactly."
									code="{ path: '/about', render: () => h(AboutPage) }"
								/>
								<RouteExample
									path="/user/:id"
									name="User Profile"
									description="Dynamic route with named parameter. Access via render({ params })."
									code={'{ path: \'/user/:id\', render: ({ id }) => h(UserProfile, { userId: id }) }'}
								/>
							</div>

							<div class="route-tips">
								<h4 class="tips-heading">Tips</h4>
								<ul>
									<li>Routes are matched in order — put specific routes before generic ones</li>
									<li>Named parameters use the <code>:paramName</code> syntax and are passed to the render function</li>
									<li>The render function receives <code>{`{ params, path }`}</code> as its argument</li>
								</ul>
							</div>
						</div>
					)}

					{activeSection === 'navigation' && (
						<div class="section-panel">
							<h3 class="panel-heading">Programmatic Navigation</h3>
							<div class="code-block">
								<div class="code-block-header">
									<span>navigate.js</span>
									<button
										class="copy-btn"
										aria-label="Copy navigate example"
										events={{ click: () => this.copyCode(NAVIGATE_EXAMPLE) }}
									>
										<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
											<rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
											<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
										</svg>
										Copy
									</button>
								</div>
								<pre class="code-block-body"><code>{NAVIGATE_EXAMPLE}</code></pre>
							</div>

							<div class="interactive-nav-demo">
								<h4 class="tips-heading">Try It</h4>
								<p>These buttons actually navigate using the router. Your current path is shown below.</p>
								<div class="current-path">
									<span class="current-path-label">Current:</span>
									<code class="current-path-value">{currentPath}</code>
								</div>
								<div class="nav-demo-buttons">
									<button
										class="nav-demo-btn"
										events={{ click: () => this.goTo('/') }}
									>
										<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
											<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
											<polyline points="9 22 9 12 15 12 15 22"></polyline>
										</svg>
										Go Home
									</button>
									<button
										class="nav-demo-btn"
										events={{ click: () => this.goTo('/docs') }}
									>
										<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
											<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
											<path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
										</svg>
										Go Docs
									</button>
									<button
										class="nav-demo-btn"
										events={{ click: () => this.goTo('/examples') }}
									>
										<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
											<rect x="3" y="3" width="7" height="7"></rect>
											<rect x="14" y="3" width="7" height="7"></rect>
											<rect x="14" y="14" width="7" height="7"></rect>
											<rect x="3" y="14" width="7" height="7"></rect>
										</svg>
										Go Examples
									</button>
									<button
										class="nav-demo-btn -accent"
										events={{ click: () => this.goTo('/learn') }}
									>
										<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
											<path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
											<path d="M6 12v5c3 3 6 3 6 3s3 0 6-3v-5"></path>
										</svg>
										Go Learn
									</button>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		);
	}
});
